import { useState, useEffect, useCallback } from 'react';
import { fetchData } from '../api';

export interface DeviceInfo {
  type: 'meter' | 'inverter' | 'hybrid';
  model?: string;
  ratedPower?: number;
  sleeping?: boolean;
  data: {
    activePower?: number;
    solarPower?: number;         // Hybrid solar power
    totalSolarPower?: number;    // Alias for compatibility
    gridPortPower?: number;
    batteryStateOfCharge?: number;
    batteryPower?: number;
    // Meter data
    meterVoltage1?: number;
    meterVoltage2?: number;
    meterVoltage3?: number;
    meterCurrent1?: number;
    meterCurrent2?: number;
    meterCurrent3?: number;
    meterPower1?: number;
    meterPower2?: number;
    meterPower3?: number;
    meterReactivePower1?: number;
    meterReactivePower2?: number;
    meterReactivePower3?: number;
    meterRealPower?: number;     // Total meter power
  };
}

export interface DeviceDataByPort {
  [portId: string]: {
    [deviceIndex: number]: DeviceInfo;
  };
}

export function useDeviceData(pollingInterval = 1000) {
  const [data, setData] = useState<DeviceDataByPort | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDeviceData = useCallback(async () => {
    try {
      const result = await fetchData('/deviceData');
      setData(result);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeviceData();
    const interval = setInterval(fetchDeviceData, pollingInterval);
    return () => clearInterval(interval);
  }, [fetchDeviceData, pollingInterval]);

  // Helper to get all devices flattened
  const getAllDevices = useCallback(() => {
    if (!data) return [];
    const devices: Array<DeviceInfo & { portId: string; deviceIndex: number }> = [];
    for (const portId in data) {
      for (const deviceIndex in data[portId]) {
        devices.push({
          ...data[portId][parseInt(deviceIndex)],
          portId,
          deviceIndex: parseInt(deviceIndex),
        });
      }
    }
    return devices;
  }, [data]);

  const getInverters = useCallback(() => {
    return getAllDevices().filter(d => d.type === 'inverter');
  }, [getAllDevices]);

  const getHybrids = useCallback(() => {
    return getAllDevices().filter(d => d.type === 'hybrid');
  }, [getAllDevices]);

  const getMeter = useCallback(() => {
    return getAllDevices().find(d => d.type === 'meter');
  }, [getAllDevices]);

  // Get aggregated battery status from all hybrids
  const getBatteryStatus = useCallback(() => {
    const hybrids = getHybrids();
    if (hybrids.length === 0) {
      return null;
    }

    let totalCapacity = 0;
    let weightedSOC = 0;
    let totalBatteryPower = 0;

    hybrids.forEach(hybrid => {
      if (!hybrid.sleeping && hybrid.data) {
        const capacity = hybrid.ratedPower || 10000;
        const soc = hybrid.data.batteryStateOfCharge || 0;
        const power = hybrid.data.batteryPower || 0;

        totalCapacity += capacity;
        weightedSOC += soc * capacity;
        totalBatteryPower += power;
      }
    });

    const averageSOC = totalCapacity > 0 ? weightedSOC / totalCapacity : 0;

    return {
      hybridCount: hybrids.length,
      averageSOC: Math.round(averageSOC),
      totalBatteryPower,
      totalCapacity,
      status: totalBatteryPower < -100 ? 'charging' : totalBatteryPower > 100 ? 'discharging' : 'idle'
    };
  }, [getHybrids]);

  return { data, loading, error, getAllDevices, getInverters, getHybrids, getMeter, getBatteryStatus, refetch: fetchDeviceData };
}
